import { useMutation } from '@apollo/client';
import Background from '@do/walrus/Background';
import SpinnerButton from '@do/walrus/SpinnerButton';
import Text from '@do/walrus/Text';
import TextInput from '@do/walrus/TextInput';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import * as Yup from 'yup';
import defaultBg from '../assets/backgrounds/Login_Default.jpg';
import copy from '../copy';
import {
  forgotPassword as ForgotPassword,
  forgotPasswordVariables
} from '../generated-types/forgotPassword';
import useDocumentTitle from '../hooks/use-document-title';
import { FORGOT_PASSWORD } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { ErrorBanner, FormWrap, PaddedContents, Wrap } from './Common';
import Header from './Header';

const passwordForgotValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required)
});

const PasswordForgot = ({ location }: RouteComponentProps): ReactElement => {
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [forgotPassword, { loading: isLoading }] = useMutation<
    ForgotPassword,
    forgotPasswordVariables
  >(FORGOT_PASSWORD);
  const { history } = useReactRouter();

  useDocumentTitle(copy.form.password_forgot_title);

  return (
    <>
      <Header isLogin={true} />
      <Background url={defaultBg} />
      <Wrap>
        <FormWrap>
          <PaddedContents>
            <h4>{copy.form.password_forgot.title}</h4>
            <Text>{copy.form.password_forgot.details}</Text>
            <Formik
              initialValues={{
                email: _get(location, 'state.email') || ''
              }}
              validationSchema={passwordForgotValidation}
              onSubmit={(values, { setErrors }) => {
                setGenericServerError(null); /* Reset displayed error */
                forgotPassword({
                  variables: {
                    forgotPasswordRequest: values
                  }
                })
                  .then(() => {
                    history.push({
                      pathname: '/login',
                      state: { success: 'forgot' }
                    });
                  })
                  .catch(handleFormError(setErrors, setGenericServerError));
              }}
            >
              {({ errors, values, touched }) => (
                <Form>
                  {genericServerError && (
                    <ErrorBanner error={genericServerError} />
                  )}

                  <TextInput
                    type="email"
                    placeholder={copy.form.password_forgot.placeholder}
                    name="email"
                    required
                    label={copy.form.password_forgot.label}
                    touched={touched.email}
                    error={errors.email}
                    value={values.email}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                  <SpinnerButton
                    className="do-u-mt--medium"
                    type="submit"
                    fullWidth
                    spinning={isLoading}
                    disabled={isLoading}
                    loadingMessage={copy.form.password_forgot.loading}
                  >
                    {copy.form.password_forgot.button_text}
                  </SpinnerButton>
                </Form>
              )}
            </Formik>
          </PaddedContents>
        </FormWrap>
      </Wrap>
    </>
  );
};

export default PasswordForgot;
